import React, { useState, useEffect, useContext, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay, EffectFade } from "swiper/modules";
import ContentLoader from "react-content-loader";
import { useNavigate } from "react-router-dom";
import { startOfDay } from "date-fns";
import Rating from "../common/Rating";
import AppContext from "../../AppContext";
import useWorks from "../../hooks/useWorks";
import useUsers from "../../hooks/useUsers";
import useLandingPage from "../../hooks/useLandingPage.js";
import useJournals from "../../hooks/useJournals";
import { PAGE_STATE } from "../../constants";
import { CartContext } from "../../contexts/CartContext.jsx";
import { langComponent } from "../../constants";

const assetsUri = import.meta.env.ASSETS_URI;

//image
import { ReactComponent as PlaySvg } from "../../assets/icons/play.svg";
import { ReactComponent as EmptySvg } from "../../assets/icons/empty-projects.svg";

import ProgramSketchUpImage from "../../assets/icons/programs/sketchUp.png";
import Program3dsMaxImage from "../../assets/icons/programs/3dsMax.png";
import ProgramRevitImage from "../../assets/icons/programs/autodeskRevit.png";
import ProgramCinema4DImage from "../../assets/icons/programs/cinema4d.png";
import ProgramUnrealImage from "../../assets/icons/programs/unreal.png";
import ProgramAutocadImage from "../../assets/icons/programs/autocad.png";
import ProgramArchicadImage from "../../assets/icons/programs/archicad.png";
import ProgramRhinoImage from "../../assets/icons/programs/rhino.png";

import ProgramVRayImage from "../../assets/icons/programs/vray.png";
import ProgramCoronaImage from "../../assets/icons/programs/corona.png";
import ProgramD5RenderImage from "../../assets/icons/programs/d5render.png";
import ProgramEnscapeImage from "../../assets/icons/programs/enscape.jpg";
import ProgramLumionImage from "../../assets/icons/programs/lumion.png";

const PROGRAMS = {
  sketchUp: 1,
  "3dsMax": 2,
  revit: 3,
  cinema4D: 4,
  unreal: 5,
  rhino: 6,
  autocad: 7,
  archicad: 8,
};

const RENDERERS = {
  vray: 1,
  corona: 2,
  lumion: 3,
  enscape: 4,
  d5render: 5,
};

//component
import Card from "./Card";
import StylishButton from "../common/StylishButton";
import { ChoiceChip } from "../common/ChoiceChip";
import ScrollDownIndicator from "../../components/common/ScrollDownIndicator";

const Header = ({ haveTab, currentTab, id, shoppingItem }) => {
  // { haveTab, currentTab, navigate }
  const { getLang, designStyles, user: userData } = useContext(AppContext);
  const navigate = useNavigate();
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [selectedProjectIndex, setSelectedProjectIndex] = useState(0);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(undefined);
  const [projects, setProjects] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);

  const { queryWorks } = useWorks();
  const { queryUsers, queryCompanies } = useUsers();
  const { queryLandingPageConfigs } = useLandingPage();
  const { queryJournals } = useJournals();
  const { isFetchDetail, setIsFetchDetail, items } = useContext(CartContext);
  const TranslationWorkExchange = getLang(langComponent.workExchange);
  const TranslationCommon = getLang(langComponent.common);

  useEffect(() => {
    if (!designStyles) return;

    const _tabs = [
      designStyles.workExchanges.find((w) => w.name.includes("3D Models")),
      designStyles.workExchanges.find((w) =>
        w.name.includes("Materials and lighting")
      ),
      designStyles.workExchanges.find((w) =>
        w.name.includes("Drawing and block CAD")
      ),
    ];

    setTabs(_tabs);
    _init();
    window.scrollTo({ top: 0 });
  }, [designStyles]);

  const _init = async () => {
    setIsFetchDetail(false);

    const _filter = {
      "designType.workExchange": selectedTab,
      type: 2,
      active: true,
      deleted: false,
    };

    if (haveTab && selectedTab) {
      _filter["designType.workExchange"] = selectedTab;
    }

    if (haveTab && !selectedTab) {
      delete _filter["designType.workExchange"];
    }

    if (!haveTab && id) {
      _filter["designType.workExchange"] = id;
    }

    const { data: _works } = await queryWorks(
      _filter,
      1,
      6,
      {
        name: 1,
        createdBy: 1,
        desc: 1,
        brief: 1,
        price: 1,
        banner: 1,
        program: 1,
        renderer: 1,
        views: 1,
        designType: 1,
        discount: 1,
        createdOn: 1,
        company: 1,
        score: 1,
      },
      {
        createdOn: -1,
      }
    );

    const { data: _users } = await queryUsers(
      { _id: { $in: _works.map((d) => d.createdBy) } },
      1,
      _works.length,
      { firstname: 1, surname: 1, avatar: 1 }
    );

    const { data: companies } = await queryCompanies(
      {
        _id: {
          $in: _works.map((w) => w.company ?? w.createdBy),
        },
      },
      null,
      -1,
      {
        _id: 1,
        name: 1,
        logo: 1,
      }
    );

    const _workIdList = _works?.map((item) => item._id);
    const { data: _promoteList } = await queryLandingPageConfigs(
      {
        work: {
          $in: _workIdList,
        },
        effectiveDate: {
          $gte: startOfDay(new Date()),
        },
        active: true,
        deleted: false,
      },
      1,
      null,
      {
        price: -1,
      },
      {
        work: 1,
        price: 1,
      }
    );

    if (userData?._id) {
      const { data: _journals } = await queryJournals({
        "details.work": {
          $in: _workIdList,
        },
        company: userData?.company, // * Company
        createdBy: userData?._id, // * User ที่ login
        active: true,
        deleted: false,
        status: 2,
      });

      setJournals(_journals);
    }

    // * filter work promote with work
    const workPromoteList = _promoteList.map((promote) => {
      const workPromote = _works.find((w) => promote.work === w._id);

      return {
        ...workPromote,
      };
    });

    // * Merge work with users and set state
    setProjects(
      _works.map((w) => {
        const userFound = _users.find((u) => u._id === w.createdBy);
        const _company = companies.find(
          (company) => w?.company === company._id
        );

        return {
          ...w,
          user: _company
            ? {
                name: _company.name,
                avatar: _company.logo,
              }
            : {
                name: `${userFound.firstname} ${userFound.surname}`,
                avatar: userFound.avatar,
              },
        };
      })
    );
    setLoading(true);
    setPageState(PAGE_STATE.IDLE);
  };

  const handleTabClick = (tabValue) => {
    if (selectedTab === tabValue) {
      setSelectedTab(null);
      return;
    }

    setSelectedTab(tabValue);
  };

  useEffect(() => {
    if (!selectedTab) return;
    setSelectedTab(selectedTab);
    _init();
  }, [selectedTab, userData?._id, items]);

  const handleClickView = (id) => {
    navigate(`/work-exchanges/${id}`);
  };

  const getProgram = (program) => {
    if (program == PROGRAMS.sketchUp) {
      return <img src={ProgramSketchUpImage} />;
    } else if (program == PROGRAMS["3dsMax"]) {
      return <img src={Program3dsMaxImage} />;
    } else if (program == PROGRAMS.revit) {
      return <img src={ProgramRevitImage} />;
    } else if (program == PROGRAMS.cinema4D) {
      return <img src={ProgramCinema4DImage} />;
    } else if (program == PROGRAMS.unreal) {
      return <img src={ProgramUnrealImage} />;
    } else if (program == PROGRAMS.rhino) {
      return <img src={ProgramRhinoImage} />;
    } else if (program == PROGRAMS.autocad) {
      return <img src={ProgramAutocadImage} />;
    } else if (program == PROGRAMS.archicad) {
      return <img src={ProgramArchicadImage} />;
    }
  };

  const getRenderers = (renderer) => {
    if (renderer == RENDERERS.vray) {
      return <img src={ProgramVRayImage} />;
    } else if (renderer == RENDERERS.corona) {
      return <img src={ProgramCoronaImage} />;
    } else if (renderer == RENDERERS.lumion) {
      return <img src={ProgramLumionImage} />;
    } else if (renderer == RENDERERS.enscape) {
      return <img src={ProgramEnscapeImage} />;
    } else if (renderer == RENDERERS.d5render) {
      return <img src={ProgramD5RenderImage} />;
    }
  };

  const skeletonLoader = (props) => (
    <div className="work-exchange-header-slider">
      <div className="swiper">
        <ContentLoader viewBox="0 0 620 782" {...props} rtl>
          <rect x="0" y="0" rx={20} ry={20} width="620" height="100%" />
        </ContentLoader>
      </div>
    </div>
  );

  // if (pageState !== PAGE_STATE.IDLE) {
  //   return <></>;
  // }

  if (projects.length == 0) {
    return (
      <div className="work-exchange-header">
        {loading ? (
          <div>
            {haveTab && (
              <div className="work-exchange-header-item-filters">
                {tabs.map((tab) => {
                  const isActive = selectedTab === tab.value;

                  return (
                    <div
                      className="work-exchange-header-item-filters__item"
                      key={tab.value}
                    >
                      <ChoiceChip
                        label={tab.name}
                        onClick={() => handleTabClick(tab.value)}
                        isSelected={isActive}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="work-exchange-header-item-loader">
            <ContentLoader width={"80%"} height={250}>
              <rect x="0" y="0" rx="3" ry="3" width="150" height="50" />
              <rect x="0" y="60" rx="50%" ry="50%" width="60" height="60" />
              <rect x="70" y="70" rx="3" ry="3" width="200" height="50" />
              <rect x="0" y="145" rx="3" ry="3" width="100%" height="20" />
              <rect x="0" y="170" rx="3" ry="3" width="100%" height="20" />
              <rect x="0" y="195" rx="3" ry="3" width="100%" height="20" />
            </ContentLoader>
          </div>
        )}
        {loading ? (
          <div className="work-exchange-header-slider">
            <div className="work-exchange-header-slider-notfound">
              <EmptySvg height={224} style={{ opacity: 0.3 }} />
              <span
                style={{
                  fontSize: 16,
                  color: "#9d9d9d",
                  marginTop: -8,
                }}
              >
                No results found
              </span>
            </div>
          </div>
        ) : (
          <>{skeletonLoader()}</>
        )}
      </div>
    );
  }

  if (projects.length == 0) return;

  const {
    _id,
    name,
    desc,
    user,
    price,
    banner,
    discount,
    score,
    program,
    renderer,
  } = projects[selectedProjectIndex];

  return (
    <div className="work-exchange-header">
      <div className="work-exchange-header-item">
        <div className="work-exchange-header-item-wrap">
          <div
            className="work-exchange-header-item-info"
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <img
              className="work-exchange-header-item__avatar"
              src={`${assetsUri}/${user.avatar}`}
              onError={({ target }) => {
                target.src = user.avatar;
              }}
            />
            <span className="work-exchange-header-item__name">{user.name}</span>
          </div>
          <div style={{ alignSelf: "flex-start" }}>
            <Rating score={score ?? 0} size={30} readOnly />
          </div>
          <span className="work-exchange-header-item__title">{name ?? ""}</span>
          <div className="work-exchange-header-item__programs">
            {getProgram(program)}
            {getRenderers(renderer)}
          </div>
          <div style={{ flexGrow: 1 }} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignSelf: "flex-start",
              gap: "0.5rem",
            }}
          >
            <span className="work-exchange-header-item__price">
              {discount > 0 && (
                <div className="work-exchange-header-item__price--discount">
                  {price.toLocaleString()}
                </div>
              )}
              <span
                className={`${
                  discount > 0
                    ? "work-exchange-header-item__price--new-price"
                    : ""
                }`}
              >
                {discount > 0
                  ? (price - discount).toLocaleString()
                  : price == 0
                  ? TranslationWorkExchange("Free")
                  : price.toLocaleString()}
                {price > 0 && (
                  <span style={{ marginLeft: "0.3rem" }}>
                    {TranslationWorkExchange("Baht")}
                  </span>
                )}
              </span>
            </span>
            <StylishButton
              label={TranslationCommon("View now")}
              icon={<PlaySvg />}
              onClick={() => handleClickView(_id)}
              size="small"
            />
          </div>
        </div>
        {haveTab && (
          <div className="work-exchange-header-item-filters">
            {tabs.map((tab) => {
              const isActive = selectedTab === tab.value;

              return (
                <div
                  className="work-exchange-header-item-filters__item"
                  key={tab.value}
                >
                  <ChoiceChip
                    label={tab.name}
                    onClick={() => handleTabClick(tab.value)}
                    isSelected={isActive}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div
        className="work-exchange-header__separator"
        style={{ flexGrow: 1, paddingRight: 32 }}
      />
      {projects?.length > 0 && (
        <Swiper
          className="work-exchange-header-slider"
          grabCursor={true}
          centeredSlides={false}
          slidesPerView={1}
          navigation={true}
          spaceBetween={0}
          effect="fade"
          pagination={{ clickable: true }}
          modules={[Pagination, Navigation, Autoplay, EffectFade]}
          loop={true}
          autoplay={{
            delay: 8000,
            disableOnInteraction: false,
          }}
          onSlideChange={(swiperCore) => {
            const { realIndex } = swiperCore;
            setSelectedProjectIndex(realIndex);
          }}
        >
          {projects.map((project, index) => (
            <SwiperSlide
              key={index}
              className="work-exchange-header-slider__item"
            >
              <Card
                {...project}
                creator={user}
                workJournals={journals}
                isHideInfo={true}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <div className="work-exchange-header-scroll-down">
        <ScrollDownIndicator />
      </div>
    </div>
  );
};

Header.propTypes = {};

export default Header;
